import { DistributionCharts } from '../api/analytics';
import { BasicEnrollment, Course } from '../models/course';
import { QuizQuestion } from '../models/quiz';

export enum AnalyticsDashBoardType {
  UNSET = 'unset',
  LEARNING_JOURNEY = 'journey',
  INSTITUTION = 'institution',
  COURSE = 'course',
  LICENSE = 'license',
  COHORTS = 'cohort',
  PRACTICE_ANALYTICS = 'scenarioAnalytics',
  QUESTION_SET = 'questionSet',
  COHORT_QUESTION_SET = 'cohortQuestionSet',
  MENTORSHIP_PROGRAM = 'mentorshipProgram',
}

export enum AnalyticsPageType {
  COURSE_ANALYTICS = 'course',
  COHORT_ANALYTICS = 'cohort',
}

export enum QuestionSetKey {
  TIMED_EXAMS = 'timed_exams',
  SURVEYS = 'surveys',
  QUIZZES = 'quizzes',
}

export enum ChartType {
  COMPARISON = 'Comparison',
  QUESTION = 'Question',
  ALL = 'All',
  LEARNERS = 'LearnersPerformance',
  ALL_LEARNERS = 'AllLearnersPerformance',
  ACROSS_ATTEMPTS = 'AcrossAttemptsPerformance',
  AVG_UNIQUE = 'AvgUniquePerformance',
  AVG_BY_COHORT = 'AveragePerformanceByCohort',
  UNIQ_RESPONDERS = 'UniqueResponders',
  ALL_RESPONDES = 'AllRespondersByQuestion',
  TEXT_QUESTION = 'TextQuestion',
  MULTIPLE_CHOICE_QUESTION = 'MultipleChoiceQuestion',
  NUMBER_QUESTION = 'NumberQuestion',
  LIKERT_SCALE_AVG = 'LikertScaleAverageResponse',
  LIKERT_SCALE_QUESTION = 'LikertScaleQuestion',
}

export const questionSetkeyMapping = {
  TimedExam: 'TIMED_EXAM',
  Quiz: 'QUIZ',
  Survey: 'SURVEY',
};

export type QuestionSet = {
  id: number;
  title: string;
  lecturePageId: number;
};
export enum ConfigurationType {
  course = 'course_analytics',
  cohort = 'cohort_analytics',
}
export type ReportConfiguration = {
  configurationType: ConfigurationType
  hasAccessToPostCourse?: boolean
  id?: number
  postCourse?: Course
  postCourseSurvey?: QuestionSet
  preCourseSurvey?: QuestionSet
  reportType?: 'Survey'
  questionMappings?: QuestionMapping[]
};

export type ExistingConfiguration = ReportConfiguration & { isLoaded: boolean };

export type AnalyticsState = {
  questionSets?: {
    [key: string]: {
      course: { [key in QuestionSetKey]?: QuestionSet[] },
      cohort: { [key in QuestionSetKey]?: QuestionSet[] },
    },
  },
  surveyQuestionsets?: {
    [key: string]: {
      course: { [key in QuestionSetKey]?: QuestionSet[] },
      cohort: { [key in QuestionSetKey]?: QuestionSet[] },
    },
  },
  practiceList?: {
    id: number;
    title: string;
  }[]
  questionSetsLoaded?: boolean
  isPracticeListLoaded?: boolean
  cohorts?: BasicEnrollment[]
  isCohortsListLoaded?: boolean
  accessibleCourses?: BasicEnrollment[]
  surveyQuestions?: {
    [surveyId: string]: QuizQuestion[]
  }
  loadedSurveyQuestions?: {
    [surveyId: string]: boolean
  }
  existingConfiguration?: {
    [key: string]: ExistingConfiguration[]
  }
  surveyCharts?: {
    [key: string]: {
      shouldReload?: boolean,
      isLoading?: boolean,
    }
  }
  surveyStats?: {
    [key: string]: {
      totalCourses: number,
      totalLearners: number,
      totalResponders: number,
      engagementPercentage?: number,
    }
  }
  distributionCharts?: {
    [key: string]: DistributionCharts
  }
  currentPrePostConfigurationId?: number | null
};

export type QrveyState = {
  isLoading: boolean;
  course?: QrveyInstance,
  cohort?: QrveyInstance,
  journey?: QrveyInstance,
  license?: QrveyInstance,
  scenarioAnalytics?: QrveyInstance,
  institution?: QrveyInstance,
  questionSet?: QrveyInstance,
  mentorshipProgram?: QrveyInstance,
  type: AnalyticsDashBoardType;
};

export type QrveyPanel = {
  title?: string;
  chartId: string,
  metricId?: string,
  qrveyid: string,
  dataSetId: string,
  userFilters?: any,
  chartType?: ChartType
  className?: string
  customTitle?: string
  titleInfo?: string
};

export type QrveyInstance = {
  token: string;
  appId: string;
  userId: string;
  host: string;
  journeyName?: string;
  pageId?: string;
  pageIds?: string[];
  userFilters?: any;
  topPageId?: string
  panelsPageId?: string
  title?: string
  panels: QrveyPanel[]
  type?: 'Survey' | 'TimedExam' | 'Quiz'
};

export type QrveyConfig = {
  qv_token: string;
  domain: string;
  app_id: string;
  user_id: string;
  page_id?: string;
  customCSSRules: string;
  custom_styles: boolean;
  automaticDownload: any;
  pageIds?: any;
  downloadingBoxText?: string;
  userFilters?: any;
  top_page_id?: string;
  panels_page_id?: string;
  dashboardId?: string;
  styles?: {
    main_color?: string
    main_text_color?: string
    secondary_color?: string
    icon_color?: string
    tab_bar_color?: String
    tab_font_color?: string
    error_color?: string
  }
  featurePermission?: {
    panels: {
      global: {
        hide_CSV: boolean,
      },
    },
  },
};

export type AnalyticsTokenProps = {
  type: AnalyticsDashBoardType,
  catalogId?: string,
  institutionId?: number,
  pageIdKey?: string
  scenarioId?: number
  questionSetId?: number
  cohortCourseIds?: number[]
  programId?: number
};

export type QuestionMapping = {
  preQuestionId: number;
  postQuestionId: number;
};

export interface AverageChangeRow {
  id: string | number;
  question: string;
  preAverage: number;
  postAverage: number;
  changePercent: number;
}
export interface QuantileDistribution {
  id: string | number;
  question: string;
  preDistribution: {
    low: number;
    medium: number;
    high: number;
  };
  postDistribution: {
    low: number;
    medium: number;
    high: number;
  };
}

